/**
 * InlineRichTextEditor — TipTap-based inline rich text editor
 *
 * Replaces bare contentEditable for text editing in the visual editor.
 * Supports: bold, italic, underline, strikethrough, links.
 * On blur: calls onSave(editor.getHTML())
 * On Escape: discards changes and restores original value.
 * Minimal chrome — appears inline within the template layout.
 */

import React, { useCallback, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';

const InlineRichTextEditor = ({ value, onSave, className, style, tag = 'span', onElementSelect, bindingPath }) => {
  const originalValue = useRef(value || '');
  const isDiscarding = useRef(false);
  const hasSaved = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        paragraph: true,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: '' },
      }),
      TextStyle,
      Color,
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'tiptap-inline-editor',
      },
      handleKeyDown: (view, event) => {
        event.stopPropagation();

        if (event.key === 'Escape') {
          event.preventDefault();
          isDiscarding.current = true;
          view.dom.blur();
          return true;
        }

        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          view.dom.blur();
          return true;
        }

        return false;
      },
    },
    onFocus() {
      originalValue.current = value || '';
      hasSaved.current = false;
      isDiscarding.current = false;
    },
    onBlur() {
      if (isDiscarding.current) {
        isDiscarding.current = false;
        hasSaved.current = false;
        return;
      }
      if (hasSaved.current) return;
      hasSaved.current = true;

      if (editor) {
        const html = editor.getHTML();
        let cleaned = html;
        if (cleaned.startsWith('<p>') && cleaned.endsWith('</p>') && (cleaned.match(/<p>/g) || []).length === 1) {
          cleaned = cleaned.slice(3, -4);
        }
        if (cleaned !== originalValue.current) {
          onSave(cleaned);
        }
      }
    },
  });

  // Update editor content when value prop changes externally (e.g., undo/redo)
  useEffect(() => {
    if (editor && !editor.isFocused && value !== undefined) {
      const currentHTML = editor.getHTML();
      let cleaned = currentHTML;
      if (cleaned.startsWith('<p>') && cleaned.endsWith('</p>') && (cleaned.match(/<p>/g) || []).length === 1) {
        cleaned = cleaned.slice(3, -4);
      }
      if (cleaned !== value) {
        editor.commands.setContent(value || '');
      }
    }
  }, [value, editor]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onElementSelect?.({ type: 'text', domNode: e.currentTarget, bindingPath });
  }, [onElementSelect, bindingPath]);

  if (!editor) return null;

  const Tag = tag || 'span';

  return (
    <Tag
      className={className || ''}
      style={{
        ...style,
        position: 'relative',
        minWidth: '20px',
        cursor: 'text',
      }}
      onClick={handleClick}
    >
      <EditorContent
        editor={editor}
        style={{
          outline: editor.isFocused ? '2px solid #6366f1' : 'none',
          outlineOffset: editor.isFocused ? '2px' : '0px',
          borderRadius: editor.isFocused ? '3px' : '2px',
          transition: 'outline 0.15s ease, box-shadow 0.15s ease',
          caretColor: '#6366f1',
          boxShadow: editor.isFocused ? '0 0 0 4px rgba(99,102,241,0.08)' : 'none',
        }}
      />
      <style>{`
        .tiptap-inline-editor.ProseMirror {
          outline: none !important;
          min-height: 1em;
          word-break: break-word;
        }
        .tiptap-inline-editor.ProseMirror p {
          margin: 0;
        }
        .tiptap-inline-editor.ProseMirror:focus {
          outline: none !important;
        }
        .tiptap-inline-editor.ProseMirror a {
          color: inherit;
          text-decoration: underline;
        }
      `}</style>
    </Tag>
  );
};

export default InlineRichTextEditor;
